import CropIcon from "@mui/icons-material/Crop";
import RefreshIcon from "@mui/icons-material/Refresh";
import { IconButton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import "cropperjs/dist/cropper.css";
import get from "lodash/get";
import React, { forwardRef, useRef, useState } from "react";
import Cropper from "react-cropper";
import MarketingFlyerModalBodyContainer from "./MarketingFlyerModalBodyContainer";

const StyledPaper = styled("div")({
    display: "flex",
    flexWrap: "wrap",
    background: "white",
    top: "50%",
    left: "50%",
    width: "1248px",
    height: "auto",
    transform: "translate(-50%, -50%)",
    position: "absolute",
    borderRadius: "3px",
    alignContent: "center",
    boxShadow: " 0 3px 7px rgba(0, 0, 0, 0.3)",
    padding: "5px",
});

const StyledContainer = styled("div")({
    display: "flex",
    flexWrap: "nowrap",
});

const StyledDiv = styled("div")(({ customWidth }) => ({
    flexGrow: "1",
    flexShrink: "0",
    flexbasis: "100%",
    width: customWidth || "auto",
}));

const StyledContainerHeaderBody = styled("div")(({ customWidth }) => ({
    padding: "5px",
    paddingRight: "15px",
    alignContent: "space-between",
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    gap: "8px",
    customWidth: customWidth || "auto",
}));

const StyledEditorView = styled("div")({
    display: "flex",
    border: "1px solid black",
    borderRadius: "3px",
    position: "relative",
    justifyContent: "center",
});

const StyledEditorButtons = styled("span")({
    alignSelf: "flex-end",
    position: "absolute",
    bottom: "0%",
    right: "0%",
    zIndex: "100",
});

const StyledPreviewStyle = styled("div")({
    display: "flex",
    height: "75%",
    width: "100%",
    overflow: "hidden",
});

const StyledCenterContent = styled("div")(({ customWidth }) => ({
    display: "flex",
    justifyContent: "center",
    width: customWidth || "auto",
}));

const StyledImgPreview = styled("div")({
    border: "1px solid black",
    overflow: "hidden",
    borderRadius: "3px",
    width: "100%",
});

const GenerateImageEditorModal = (props) => {
    const {
        reportName,
        editorDimensions,
        modalContent,
        setMarketingFlyerPhoto,
        resetPhoto,
        handleCloseModal,
        timeOnMarketInput,
        setDealParty,
        compTab,
        generateCompReport,
        selectedCompId,
    } = props;

    const { dealParty, timeOnMarket, marketingFlyerPhoto, marketingFlyerPhotos } = modalContent;
    const cropperRef = useRef(null);

    const [selectedMarketingFlyerPhotoSrcUrl, setSelectedMarketingFlyerPhotoSrcUrl] = useState("");
    const [originalSelectedMarketingFlyerPhotoSrcUrl, setOriginalSelectedMarketingFlyerPhotoSrcUrl] = useState("");

    const getCropData = () => {
        if (typeof cropperRef.current.cropper !== "undefined") {
            const dataUrl = cropperRef.current.cropper.getCroppedCanvas().toDataURL();
            setSelectedMarketingFlyerPhotoSrcUrl(dataUrl);

            // Fallback logic for Comp Photo
            setMarketingFlyerPhoto(dataUrl);
        }
    };

    const handleGenerateMarketingFlyerReport = () => {
        getCropData();
        const croppedPhoto = cropperRef.current.cropper.getCroppedCanvas().toDataURL();
        const croppedPhotoSlice = croppedPhoto.split(",")[1];
        generateCompReport(compTab, false, null, "MarketingFlyer", null, dealParty, timeOnMarket, croppedPhotoSlice);
    };

    const handleSelectPhotoFromDropdown = (selectedPhotoId) => {
        const selectedPhoto = marketingFlyerPhotos.find((photo) => photo.mediaAssetKey === selectedPhotoId);
        setOriginalSelectedMarketingFlyerPhotoSrcUrl(get(selectedPhoto, "url", ""));
        setSelectedMarketingFlyerPhotoSrcUrl(get(selectedPhoto, "url", ""));
    };

    const handleResetPhoto = () => {
        setSelectedMarketingFlyerPhotoSrcUrl(originalSelectedMarketingFlyerPhotoSrcUrl);

        // Fallback logic for Comp Photo
        resetPhoto();
    };

    // Fallback logic for Comp Photo
    const shouldShowPreview =
        marketingFlyerPhotos.length === 0
            ? marketingFlyerPhoto.length > 0
            : selectedMarketingFlyerPhotoSrcUrl.length > 0;
    const shouldDisableButtons =
        marketingFlyerPhotos.length === 0
            ? marketingFlyerPhoto.length === 0
            : selectedMarketingFlyerPhotoSrcUrl.length === 0;

    return (
        <React.Fragment>
            <StyledPaper>
                <StyledCenterContent customWidth="100%">
                    <EditorView
                        editorDimensions={editorDimensions}
                        marketingFlyerPhoto={marketingFlyerPhoto}
                        marketingFlyerPhotos={marketingFlyerPhotos}
                        selectedMarketingFlyerPhotoSrcUrl={selectedMarketingFlyerPhotoSrcUrl}
                        handleResetPhoto={handleResetPhoto}
                        ref={cropperRef}
                        getCropData={getCropData}
                        shouldDisableButtons={shouldDisableButtons}
                    />
                </StyledCenterContent>
                <StyledCenterContent customWidth="100%">
                    <StyledContainer>
                        <StyledContainerHeaderBody customWidth="75%">
                            <StyledDiv customWidth="100%">
                                <ModalHeader reportName={reportName} />
                            </StyledDiv>
                            <MarketingFlyerModalBodyContainer
                                handleCloseModal={handleCloseModal}
                                handleGenerateMarketingFlyerReport={handleGenerateMarketingFlyerReport}
                                handleSelectPhotoFromDropdown={handleSelectPhotoFromDropdown}
                                modalContent={modalContent}
                                setDealParty={setDealParty}
                                timeOnMarketInput={timeOnMarketInput}
                                selectedCompId={selectedCompId}
                                shouldDisableButtons={shouldDisableButtons}
                            />
                        </StyledContainerHeaderBody>
                        <StyledDiv customWidth="25%">
                            <Typography
                                gutterBottom={true}
                                style={{ fontWeight: "bold", marginBottom: "25px" }}
                                variant="h6"
                            >{`Preview`}</Typography>
                            {shouldShowPreview ? <Preview /> : null}
                        </StyledDiv>
                    </StyledContainer>
                </StyledCenterContent>
            </StyledPaper>
        </React.Fragment>
    );
};

const EditorView = forwardRef((props, ref) => {
    const {
        editorDimensions,
        marketingFlyerPhoto,
        marketingFlyerPhotos,
        selectedMarketingFlyerPhotoSrcUrl,
        handleResetPhoto,
        getCropData,
        shouldDisableButtons,
    } = props;

    return (
        <React.Fragment>
            <StyledEditorView style={{ height: editorDimensions.height, width: editorDimensions.width }}>
                <StyledEditorButtons>
                    <IconButton disabled={shouldDisableButtons} color="primary">
                        <CropIcon onClick={() => getCropData()} />
                    </IconButton>
                    <IconButton disabled={shouldDisableButtons} color="primary">
                        <RefreshIcon onClick={handleResetPhoto} />
                    </IconButton>
                </StyledEditorButtons>
                <Cropper
                    ref={ref}
                    style={{ height: "100%", width: "100%" }}
                    zoomTo={0.5}
                    initialAspectRatio={153 / 55}
                    preview=".img-preview"
                    src={marketingFlyerPhotos.length === 0 ? marketingFlyerPhoto : selectedMarketingFlyerPhotoSrcUrl}
                    viewMode={0}
                    minCropBoxHeight={10}
                    minCropBoxWidth={10}
                    minCanvasWidth={1224}
                    minCanvasHeight={440}
                    cropBoxResizable={false}
                    cropBoxMovable={false}
                    dragMode={"move"}
                    background={false}
                    responsive={true}
                    autoCropArea={1}
                    toggleDragModeOnDblclick={false}
                    checkOrientation={false} // https://github.com/fengyuanchen/cropperjs/issues/671
                />
            </StyledEditorView>
        </React.Fragment>
    );
});

const ModalHeader = (props) => {
    return (
        <React.Fragment>
            <Typography
                gutterBottom={true}
                style={{ fontWeight: "bold", marginBottom: "25px" }}
                variant="h6"
            >{`Create A ${props.reportName} Report`}</Typography>
        </React.Fragment>
    );
};

const Preview = (props) => {
    return (
        <React.Fragment>
            <StyledPreviewStyle>
                <StyledImgPreview className="img-preview"></StyledImgPreview>
            </StyledPreviewStyle>
        </React.Fragment>
    );
};

export default GenerateImageEditorModal;
